import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as EmpDB from '@/lib/employeeDb';
import { formatTRY } from '@/lib/currencyFormat';
import EmployeeFormModal from '@/components/feature/EmployeeFormModal';
import Badge from '@/components/base/Badge';
import { staffStatus } from '@/lib/statusDefs';

const POSITIONS = ['Yönetici', 'Satış Personeli', 'Kasa', 'Muhasebe', 'Depo Personeli', 'Teslimat Personeli', 'Montaj Personeli', 'Servis Personeli'];
const DEPARTMENTS = ['Yönetim', 'Satış', 'Finans', 'Depo', 'Teslimat', 'Montaj', 'Servis', 'Muhasebe'];
const STATUSES = ['Aktif', 'İzinli', 'Raporlu', 'İşten Ayrıldı', 'Askıda'];

export default function StaffPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const srcParam = searchParams.get('src');
  const statusParam = searchParams.get('status');
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterPosition, setFilterPosition] = useState('');
  const [filterStatus, setFilterStatus] = useState(statusParam === 'active' ? 'Aktif' : statusParam === 'on_leave' ? 'İzinli' : '');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [toast, setToast] = useState<{ msg: string; type: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await EmpDB.getEmployees();
    if (err) { setError(err); setLoading(false); return; }
    setEmployees(data || []);
    setError(null);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg: string, type: string = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const filtered = useMemo(() => {
    let list = employees;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(e =>
        (e.full_name || '').toLowerCase().includes(q) ||
        (e.employee_no || '').toLowerCase().includes(q) ||
        (e.phone || '').includes(q) ||
        (e.email || '').toLowerCase().includes(q)
      );
    }
    if (filterDept) list = list.filter(e => e.department === filterDept);
    if (filterPosition) list = list.filter(e => e.position === filterPosition);
    if (filterStatus) list = list.filter(e => e.status === filterStatus);
    return list;
  }, [employees, search, filterDept, filterPosition, filterStatus]);

  const summary = useMemo(() => {
    const active = employees.filter(e => e.status === 'Aktif').length;
    const onLeave = employees.filter(e => e.status === 'İzinli' || e.status === 'Raporlu').length;
    return { total: employees.length, active, onLeave };
  }, [employees]);

  const handleSave = async (data: any) => {
    if (editData) {
      const { data: _, error: err } = await EmpDB.updateEmployee(editData.id, data);
      if (err) { showToast('Güncelleme başarısız: ' + err, 'error'); return; }
      showToast('Personel güncellendi');
    } else {
      const { data: noData } = await EmpDB.generateEmployeeNo();
      const employeeNo = noData || `PER-${new Date().getFullYear()}-001`;
      const { data: _, error: err } = await EmpDB.createEmployee({ ...data, employee_no: employeeNo });
      if (err) { showToast('Personel eklenemedi: ' + err, 'error'); return; }
      showToast('Personel eklendi');
    }
    setShowForm(false);
    setEditData(null);
    load();
  };

  const handleDelete = async (emp: any) => {
    if (!confirm(`${emp.full_name} silinecek. Emin misiniz?`)) return;
    const { error: err } = await EmpDB.deleteEmployee(emp.id);
    if (err) { showToast('Silinemedi: ' + err, 'error'); return; }
    showToast('Personel silindi');
    load();
  };

  const handleToggleStatus = async (emp: any) => {
    const newStatus = emp.status === 'Aktif' ? 'İşten Ayrıldı' : 'Aktif';
    const { error: err } = await EmpDB.updateEmployee(emp.id, { status: newStatus });
    if (err) { showToast('Durum güncellenemedi: ' + err, 'error'); return; }
    showToast(`${emp.full_name} → ${newStatus}`);
    load();
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin mb-4"></div>
        <h2 className="text-lg font-semibold text-foreground-700">Personel yükleniyor...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4"><i className="ri-error-warning-line text-2xl text-red-400"></i></div>
        <h2 className="text-lg font-semibold text-foreground-700">Yükleme başarısız</h2>
        <p className="text-sm text-foreground-400 mt-1">{error}</p>
        <button onClick={load} className="btn-primary mt-4 cursor-pointer flex items-center gap-2 whitespace-nowrap"><i className="ri-refresh-line"></i>Tekrar Dene</button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {toast && (
        <div className={`fixed top-4 right-4 z-[70] px-4 py-3 rounded-xl shadow-lg text-sm font-medium transition-all ${
          toast.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
        }`}>
          {toast.msg}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">Personel Yönetimi</h2>
          <p className="text-sm text-foreground-400 mt-0.5">Tüm personel kayıtları ve özlük işlemleri</p>
        </div>
        <button onClick={() => { setEditData(null); setShowForm(true); }} className="btn-primary cursor-pointer whitespace-nowrap flex items-center gap-2">
          <i className="ri-user-add-line"></i>Yeni Personel
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="stat-card"><p className="text-xs text-foreground-400">Toplam Personel</p><p className="text-2xl font-bold text-foreground-900 mt-1">{summary.total}</p></div>
        <div className="stat-card"><p className="text-xs text-foreground-400">Aktif</p><p className="text-2xl font-bold text-emerald-600 mt-1">{summary.active}</p></div>
        <div className="stat-card"><p className="text-xs text-foreground-400">İzinli/Raporlu</p><p className="text-2xl font-bold text-amber-600 mt-1">{summary.onLeave}</p></div>
      </div>

      <div className="stat-card">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Ad, no, telefon veya e-posta ile ara..." className="input-base w-full pl-9 text-sm" />
          </div>
          <select value={filterDept} onChange={e => setFilterDept(e.target.value)} className="input-base text-sm">
            <option value="">Tüm Departmanlar</option>
            {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={filterPosition} onChange={e => setFilterPosition(e.target.value)} className="input-base text-sm">
            <option value="">Tüm Pozisyonlar</option>
            {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="input-base text-sm">
            <option value="">Tüm Durumlar</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {(search || filterDept || filterPosition || filterStatus) && (
            <button onClick={() => { setSearch(''); setFilterDept(''); setFilterPosition(''); setFilterStatus(''); }} className="text-xs text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap">Filtreleri Temizle</button>
          )}
        </div>

        {employees.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center">
              <i className="ri-team-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-sm text-foreground-500">Henüz personel kaydı yok</p>
            <button onClick={() => { setEditData(null); setShowForm(true); }} className="btn-primary cursor-pointer whitespace-nowrap">İlk Personeli Ekle</button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center">
              <i className="ri-filter-off-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-sm text-foreground-500">Filtrelere uygun personel bulunamadı</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto -mx-5">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="border-b border-background-200">
                    <th className="table-header">Personel No</th>
                    <th className="table-header">Ad Soyad</th>
                    <th className="table-header">Pozisyon</th>
                    <th className="table-header">Departman</th>
                    <th className="table-header">Telefon</th>
                    <th className="table-header">Maaş</th>
                    <th className="table-header">Durum</th>
                    <th className="table-header text-right">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(emp => (
                    <tr key={emp.id} className="border-b border-background-100 hover:bg-background-50 transition-colors cursor-pointer" onClick={() => navigate(`/staff/${emp.id}`)}>
                      <td className="table-cell font-mono text-xs text-foreground-500">{emp.employee_no}</td>
                      <td className="table-cell font-medium text-foreground-800">{emp.full_name}</td>
                      <td className="table-cell text-foreground-500">{emp.position || '—'}</td>
                      <td className="table-cell text-foreground-500">{emp.department || '—'}</td>
                      <td className="table-cell text-foreground-500 text-sm">{emp.phone || '—'}</td>
                      <td className="table-cell font-medium">{formatTRY(emp.salary || 0)}</td>
                      <td className="table-cell"><Badge badgeClass={staffStatus.badgeClass(emp.status)} icon={staffStatus.icon(emp.status)}>{emp.status}</Badge></td>
                      <td className="table-cell text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => { setEditData(emp); setShowForm(true); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 cursor-pointer" title="Düzenle"><i className="ri-edit-line text-foreground-400"></i></button>
                          <button onClick={() => handleToggleStatus(emp)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 cursor-pointer" title={emp.status === 'Aktif' ? 'Pasife Al' : 'Aktif Et'}>
                            <i className={emp.status === 'Aktif' ? 'ri-toggle-line text-foreground-400' : 'ri-toggle-fill text-foreground-300'}></i>
                          </button>
                          <button onClick={() => handleDelete(emp)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer" title="Sil"><i className="ri-delete-bin-line text-red-400"></i></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden space-y-2.5">
              {filtered.map(emp => (
                <div key={emp.id} onClick={() => navigate(`/staff/${emp.id}`)} className="bg-background-50 border border-background-200/70 rounded-xl p-4 space-y-3 cursor-pointer hover:border-background-300/60 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{emp.full_name}</p>
                      <p className="text-xs text-foreground-400">{emp.employee_no} · {emp.position || '—'}</p>
                    </div>
                    <Badge badgeClass={staffStatus.badgeClass(emp.status)} icon={staffStatus.icon(emp.status)}>{emp.status}</Badge>
                  </div>
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span>{emp.department || '—'}</span>
                    <span className="font-medium text-foreground-700">{formatTRY(emp.salary || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{emp.phone || '—'}</span>
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <button onClick={() => { setEditData(emp); setShowForm(true); }} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-background-100 cursor-pointer"><i className="ri-edit-line text-sm text-foreground-400"></i></button>
                      <button onClick={() => handleToggleStatus(emp)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-background-100 cursor-pointer"><i className={emp.status === 'Aktif' ? 'ri-toggle-line text-sm text-foreground-400' : 'ri-toggle-fill text-sm text-foreground-300'}></i></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <EmployeeFormModal isOpen={showForm} onClose={() => { setShowForm(false); setEditData(null); }} onSave={handleSave} initialData={editData} />
    </div>
  );
}